import {getTestProps} from '@benchling/js-test-utils';
import {Box, Center, Loader, Tabs, Text, createStyles} from '@mantine/core';
import React, {useCallback, useEffect} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {setWindowTitle} from 'BenchlingUtil';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';

import BatchesPage from './BatchesPage';
import {MODEL_HUB_INFERENCE_BANNER_HEIGHT_PX} from './ModelHubConstants';
import ModelHubContent from './ModelHubContent';
import ModelHubInferenceBanner from './ModelHubInferenceBanner';
import type {ModelHubPage} from './ModelHubPages';
import {pathForPage, pathForRoute, type ModelHubRoute} from './modelHubRoutes';
import NewPredictionPage from './new-prediction/NewPredictionPage';
import PredictionsPage from './PredictionsPage';
import {requireScientificModel, type ScientificModelNode} from './scientific-models/ScientificModelMetadata';
import {
  type ScientificModelRegistry,
  useScientificModelRegistry,
} from './scientific-models/useAvailableScientificModels';

export type CoreModelHubRoute = Extract<
  ModelHubRoute,
  {kind: 'models' | 'predictions' | 'batches' | 'new-prediction'}
>;

interface CoreModelHubViewProps {
  route: CoreModelHubRoute;
}

export default function CoreModelHubView({route}: CoreModelHubViewProps): JSX.Element {
  const result = useScientificModelRegistry();
  // These replace the whole view, so they fill the host region. That region is shorter than the
  // viewport in core, hence `100%` rather than `100vh`.
  if (result.type === 'loading') {
    return (
      <Center h="100%">
        <Loader size="md" />
      </Center>
    );
  }
  if (result.type === 'error') {
    return (
      <Center h="100%">
        <Text color="red">Error loading models: {result.error.message}</Text>
      </Center>
    );
  }
  return <LoadedCoreModelHubView registry={result.registry} route={route} />;
}

function LoadedCoreModelHubView({
  registry,
  route,
}: {
  registry: ScientificModelRegistry;
  route: CoreModelHubRoute;
}): JSX.Element {
  const {classes} = useStyles();

  // Fires once per visit rather than per tab, since navigating between tabs re-renders this component
  // instead of remounting it.
  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODEL_HUB_VIEWED);
    setWindowTitle('Model Hub');
  }, []);

  const handleNavigate = useCallback((page: ModelHubPage) => {
    navigateToURL(pathForPage(page));
  }, []);
  const handleNewPrediction = useCallback((model?: ScientificModelNode) => {
    navigateToURL(
      pathForRoute(
        model == null ? {kind: 'new-prediction'} : {kind: 'new-prediction', model: model.externalReference}
      )
    );
  }, []);
  const handleSelectBatch = useCallback((batchId: string) => {
    navigateToURL(pathForRoute({kind: 'batch-detail', id: batchId}));
  }, []);
  const handleSelectPrediction = useCallback((predictionId: string) => {
    navigateToURL(pathForRoute({kind: 'prediction-detail', id: predictionId}));
  }, []);
  const handleBackFromNewPrediction = useCallback(() => {
    navigateToURL(pathForPage('models'));
  }, []);
  const handleNewPredictionSubmitted = useCallback(() => {
    navigateToURL(pathForPage('batches'));
  }, []);

  if (route.kind === 'new-prediction') {
    return (
      <NewPredictionPage
        availableModels={registry.availableStructurePredictionModels}
        onBack={handleBackFromNewPrediction}
        onSubmitted={handleNewPredictionSubmitted}
        preselectedModel={
          route.model == null
            ? undefined
            : requireScientificModel(registry.catalogByExternalReference, route.model)
        }
      />
    );
  }

  return (
    <Box className={classes.container} {...getTestProps({component: 'CoreModelHubView', element: 'root'})}>
      <Box className={classes.titleRow}>
        <Text size="xl">Model Hub</Text>
      </Box>
      <Tabs
        classNames={{root: classes.tabs, panel: classes.tabPanel}}
        onTabChange={(page) => {
          handleNavigate(page as ModelHubPage);
        }}
        value={route.kind}
      >
        <Tabs.List>
          <Tabs.Tab value="models">Models</Tabs.Tab>
          <Tabs.Tab value="predictions">Predictions</Tabs.Tab>
          <Tabs.Tab value="batches">Batches</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value={route.kind}>
          {route.kind === 'models' && (
            <ModelHubContent
              models={registry.availableModels}
              onNewPrediction={handleNewPrediction}
              withTitle={false}
            />
          )}
          {route.kind === 'predictions' && (
            <PredictionsPage
              catalogByExternalReference={registry.catalogByExternalReference}
              models={registry.availableModels}
              onNewPrediction={handleNewPrediction}
              onSelectPrediction={handleSelectPrediction}
              withTitle={false}
            />
          )}
          {route.kind === 'batches' && (
            <BatchesPage
              catalogByExternalReference={registry.catalogByExternalReference}
              onNewPrediction={handleNewPrediction}
              onSelectBatch={handleSelectBatch}
              withTitle={false}
            />
          )}
        </Tabs.Panel>
      </Tabs>
      <Box className={classes.stickyBannerWrapper}>
        <ModelHubInferenceBanner />
      </Box>
    </Box>
  );
}

// Core's full-screen views inset their content past the global nav sidebar.
const NAV_INSET_PX = 90;

const useStyles = createStyles((theme) => ({
  container: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    padding: `${theme.spacing.lg} ${theme.spacing.xxl} ${MODEL_HUB_INFERENCE_BANNER_HEIGHT_PX}px ${NAV_INSET_PX}px`,
    width: '100%',
  },
  stickyBannerWrapper: {
    bottom: 0,
    left: 0,
    paddingLeft: NAV_INSET_PX,
    paddingRight: theme.spacing.xxl,
    position: 'fixed',
    right: 90,
    zIndex: 100,
  },
  // The single scroll owner for pages that grow. Pages that instead bound themselves to `height: 100%`
  // and scroll an inner region fit here exactly, so either way there is one scrollbar — but only as long
  // as no page forces a minimum height. A viewport-relative minimum is taller than this region, which
  // both makes this scroll unconditionally and adds a second scrollbar next to the page's own.
  tabPanel: {
    flex: '1 1 auto',
    marginTop: theme.spacing.md,
    minHeight: 0,
    overflow: 'auto',
  },
  // Lays the list and its panel out as a column, so the panel takes the remaining height.
  tabs: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    minHeight: 0,
  },
  titleRow: {
    flex: '0 0 auto',
    marginBottom: theme.spacing.lg,
  },
}));
